import { useState } from 'react';
import { api } from '../lib/api.js';
import { useFetch } from '../hooks/useFetch.js';
import { Button, Card } from './ui.jsx';
import { SetupCallLink } from './SetupCall.jsx';

const LABELS = {
  creator: {
    profile: 'Create your profile',
    applied: 'Apply to a program',
    link: 'Get your tracking link',
  },
  brand: {
    profile: 'Add your company',
    program: 'List a program',
    tracking: 'Connect conversion tracking',
    billing: 'Add a payment method',
  },
};

/**
 * "Finish setting up" card for the two dashboard overviews.
 *
 * Reads GET /api/onboarding. It renders nothing when the endpoint fails, when
 * the user has no such role, when everything is done, or after they hide it: a
 * checklist that can't be trusted or dismissed is worse than none.
 */
export function OnboardingChecklist({ side, className = '' }) {
  const { data, error } = useFetch('/onboarding');
  const [hidden, setHidden] = useState(false);

  const state = data?.[side];
  if (error || !state || hidden || state.dismissed) return null;

  const labels = LABELS[side];
  const entries = Object.entries(state.steps ?? {}).filter(([key]) => labels[key]);
  if (!entries.length || entries.every(([, done]) => done)) return null;

  const doneCount = entries.filter(([, done]) => done).length;

  async function hide() {
    setHidden(true);
    try {
      await api.patch(`/onboarding/${side}`, { dismissed: true });
    } catch {
      // Hidden for this visit either way; it will come back next time.
    }
  }

  const to =
    side === 'brand'
      ? '/start/brand'
      : state.intentProgramSlug
        ? `/programs/${state.intentProgramSlug}/apply`
        : '/programs';

  return (
    <Card className={`p-4 sm:p-5 ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Finish setting up</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            {doneCount} of {entries.length} done. Nothing here is required, and you can leave at
            any point.
          </p>
        </div>
        <div className="flex gap-2">
          <Button to={to} size="sm">
            Continue setup
          </Button>
          <Button variant="ghost" size="sm" onClick={hide}>
            Hide
          </Button>
        </div>
      </div>

      <ul className="mt-4 space-y-2">
        {entries.map(([key, done]) => (
          <li key={key} className="flex items-center gap-2.5 text-sm">
            <span
              aria-hidden="true"
              className={`flex size-5 shrink-0 items-center justify-center rounded-full border text-2xs font-semibold ${
                done
                  ? 'border-positive/40 bg-positive-soft text-positive'
                  : 'border-rule-strong bg-paper-raised text-transparent'
              }`}
            >
              ✓
            </span>
            <span className={done ? 'text-ink-muted line-through' : 'text-ink'}>
              {/* A draft already exists: the job is publishing it, not listing another. */}
              {key === 'program' && side === 'brand' && state.draftProgramId
                ? 'Publish your program'
                : labels[key]}
              <span className="sr-only">{done ? ' (done)' : ' (not done yet)'}</span>
            </span>
          </li>
        ))}
      </ul>

      <SetupCallLink
        topic={side === 'brand' ? 'setting up my program' : 'setting up my creator profile'}
        className="mt-4 border-t border-rule pt-3"
      />
    </Card>
  );
}
